import { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Clock,
  MapPin,
  Download,
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
} from "lucide-react";
import {
  generateCleaningDates,
  downloadICSFile,
  generateGoogleCalendarUrl,
  generateOutlookCalendarUrl,
} from "./utils";

// Static styles object — defined at module scope so it never re-renders
const HAS_EVENT_STYLE = {
  hasEvent: {
    backgroundColor: "#E8F5E9",
    color: "#2D5016",
    fontWeight: "bold",
  },
};

export const CalendarTab = ({ recurringBookings, onSwitchToRecurring }) => {
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [calendarExportModal, setCalendarExportModal] = useState(false);
  const [selectedEventForExport, setSelectedEventForExport] = useState(null);

  const allCleaningDates = useMemo(() => {
    const dates = [];
    recurringBookings.forEach((booking) => {
      const bookingDates = generateCleaningDates(booking, 6);
      dates.push(...bookingDates);
    });
    return dates.sort((a, b) => a.date - b.date);
  }, [recurringBookings]);

  const getEventsForDate = useCallback(
    (date) =>
      allCleaningDates.filter(
        (event) => event.date.toDateString() === date.toDateString()
      ),
    [allCleaningDates]
  );

  const datesWithEvents = useMemo(
    () => allCleaningDates.map((event) => event.date),
    [allCleaningDates]
  );

  const calendarModifiers = useMemo(
    () => ({ hasEvent: datesWithEvents }),
    [datesWithEvents]
  );

  const eventsInMonth = useMemo(() => {
    const monthStart = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      1
    );
    const monthEnd = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + 1,
      0
    );
    return allCleaningDates.filter(
      (event) => event.date >= monthStart && event.date <= monthEnd
    );
  }, [allCleaningDates, calendarMonth]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-white border-[#E2E8E0] lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-[#1A2E12] flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#2D5016]" />
              Cleaning Schedule
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDate = new Date(calendarMonth);
                  newDate.setMonth(newDate.getMonth() - 1);
                  setCalendarMonth(newDate);
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium text-[#1A2E12] min-w-[140px] text-center">
                {calendarMonth.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDate = new Date(calendarMonth);
                  newDate.setMonth(newDate.getMonth() + 1);
                  setCalendarMonth(newDate);
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <CalendarComponent
              mode="single"
              selected={selectedCalendarDate}
              onSelect={setSelectedCalendarDate}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              className="rounded-md border w-full"
              modifiers={calendarModifiers}
              modifiersStyles={HAS_EVENT_STYLE}
              data-testid="cleaning-calendar"
            />
            <div className="flex items-center gap-4 mt-4 text-sm text-[#5C6B57]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#E8F5E9] border border-[#2D5016]"></div>
                <span>Scheduled cleaning</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#2D5016]"></div>
                <span>Selected date</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8E0]">
          <CardHeader>
            <CardTitle className="text-[#1A2E12] text-lg">
              {selectedCalendarDate
                ? selectedCalendarDate.toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })
                : "Upcoming Cleans"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedCalendarDate ? (
              <div className="space-y-3">
                {getEventsForDate(selectedCalendarDate).length === 0 ? (
                  <p className="text-[#5C6B57] text-center py-4">
                    No cleans scheduled for this date
                  </p>
                ) : (
                  getEventsForDate(selectedCalendarDate).map((event, idx) => (
                    <div
                      key={`${event.booking.id}-${idx}`}
                      className="bg-[#F9FAF9] rounded-xl p-4 border border-[#E2E8E0]"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-[#1A2E12]">
                          {event.booking.service_name}
                        </h4>
                        <Badge className="bg-[#E8F5E9] text-[#2D5016]">
                          {event.booking.frequency}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-[#5C6B57] mb-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          <span>{event.booking.preferred_time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">
                            {event.booking.address}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full border-[#2D5016] text-[#2D5016] hover:bg-[#E8F5E9]"
                        onClick={() => {
                          setSelectedEventForExport({
                            event,
                            date: event.date,
                          });
                          setCalendarExportModal(true);
                        }}
                        data-testid={`add-to-calendar-${event.booking.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Add to Calendar
                      </Button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {eventsInMonth.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarDays className="w-12 h-12 mx-auto mb-3 text-[#E2E8E0]" />
                    <p className="text-[#5C6B57]">
                      No cleans scheduled this month
                    </p>
                    <Button
                      variant="link"
                      className="text-[#2D5016]"
                      onClick={onSwitchToRecurring}
                    >
                      Set up a recurring clean
                    </Button>
                  </div>
                ) : (
                  eventsInMonth.map((event, idx) => (
                    <div
                      key={`upcoming-${event.booking.id}-${idx}`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAF9] transition-colors cursor-pointer"
                      onClick={() => setSelectedCalendarDate(event.date)}
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-[#E8F5E9] rounded-lg flex flex-col items-center justify-center">
                        <span className="text-xs text-[#5C6B57]">
                          {event.date.toLocaleDateString("en-GB", {
                            weekday: "short",
                          })}
                        </span>
                        <span className="text-lg font-bold text-[#2D5016]">
                          {event.date.getDate()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1A2E12] truncate">
                          {event.booking.service_name}
                        </p>
                        <p className="text-sm text-[#5C6B57]">
                          {event.booking.preferred_time} -{" "}
                          {event.booking.postcode}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#5C6B57]" />
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={calendarExportModal}
        onOpenChange={setCalendarExportModal}
      >
        <DialogContent
          className="max-w-md"
          aria-describedby="calendar-export-description"
        >
          <DialogHeader>
            <DialogTitle className="text-[#1A2E12]">Add to Calendar</DialogTitle>
          </DialogHeader>
          {selectedEventForExport && (
            <div className="space-y-4 mt-4">
              <p
                id="calendar-export-description"
                className="sr-only"
              >
                Export your cleaning appointment to your preferred calendar application
              </p>
              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <h4 className="font-semibold text-[#1A2E12] mb-1">
                  {selectedEventForExport.event.booking.service_name}
                </h4>
                <p className="text-sm text-[#5C6B57]">
                  {selectedEventForExport.date.toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}{" "}
                  at {selectedEventForExport.event.booking.preferred_time}
                </p>
              </div>

              <p className="text-sm text-[#5C6B57]">Choose your calendar:</p>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => {
                    window.open(
                      generateGoogleCalendarUrl(
                        selectedEventForExport.event.booking,
                        selectedEventForExport.date
                      ),
                      "_blank"
                    );
                    toast.success("Opening Google Calendar...");
                  }}
                  data-testid="export-google-calendar"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span>Google Calendar</span>
                  <ExternalLink className="w-4 h-4 ml-auto text-[#5C6B57]" />
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => {
                    downloadICSFile(
                      selectedEventForExport.event.booking,
                      selectedEventForExport.date
                    );
                    toast.success("Calendar file downloaded!");
                  }}
                  data-testid="export-apple-calendar"
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                      fill="#000"
                    />
                  </svg>
                  <span>Apple Calendar</span>
                  <Download className="w-4 h-4 ml-auto text-[#5C6B57]" />
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => {
                    window.open(
                      generateOutlookCalendarUrl(
                        selectedEventForExport.event.booking,
                        selectedEventForExport.date
                      ),
                      "_blank"
                    );
                    toast.success("Opening Outlook Calendar...");
                  }}
                  data-testid="export-outlook-calendar"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#0078D4"
                      d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V5.8l-.3.01-.3.02-.3.03-.3.03-.3.04-.3.04-.3.04-.3.05-.3.05-.3.05-.3.05-.3.05-.3.05-.3.05-.3.05-.3.05v5.08q.9.59 1.5 1.5.6.91.6 2.04v2.57l.3.04.3.05.3.05.3.05.3.05.3.05V12h1.57q.44 0 .75.3.3.3.3.75zm-6-8.25v3h3v-3h-3zm0 4.5v3h3v-3h-3zm-4.5-4.5v3h3v-3h-3zm0 4.5v3h3v-3h-3z"
                    />
                  </svg>
                  <span>Outlook Calendar</span>
                  <ExternalLink className="w-4 h-4 ml-auto text-[#5C6B57]" />
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => {
                    downloadICSFile(
                      selectedEventForExport.event.booking,
                      selectedEventForExport.date
                    );
                    toast.success("Calendar file downloaded!");
                  }}
                  data-testid="export-ics-file"
                >
                  <FileText className="w-5 h-5 text-[#5C6B57]" />
                  <span>Download .ics file</span>
                  <Download className="w-4 h-4 ml-auto text-[#5C6B57]" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
